<!--
/**
 * GeofenceConfiguration.vue
 * Admin page to manage geofence zones with interactive Leaflet.js map
 */
-->
<template>
  <div class="orangehrm-background-container">
    <div class="orangehrm-card-container">
      <!-- Header -->
      <div class="geofence-header">
        <div>
          <oxd-text tag="h6" class="orangehrm-main-title">
            🗺️ Konfigurasi Geofence
          </oxd-text>
          <oxd-text tag="p" class="geofence-subtitle">
            Atur zona area yang diizinkan untuk absensi karyawan
          </oxd-text>
        </div>
        <oxd-button
          label="Tambah Zona"
          icon-name="plus"
          display-type="secondary"
          @click="openAddModal"
        />
      </div>

      <oxd-divider />

      <!-- Map Container -->
      <div class="geofence-map-section">
        <div class="geofence-map-label">
          <span class="geofence-map-badge">Live Map</span>
          Klik pada peta untuk menambah zona baru
        </div>
        <div
          id="geofence-leaflet-map"
          ref="mapContainer"
          class="geofence-map-container"
        ></div>
      </div>

      <oxd-divider />

      <!-- Zones List -->
      <div class="geofence-zones-section">
        <oxd-text tag="p" class="geofence-section-title"
          >Zona Aktif ({{ zones.length }})</oxd-text
        >

        <div v-if="isLoading" class="geofence-loading">
          <oxd-loading-spinner />
        </div>

        <div v-else-if="zones.length === 0" class="geofence-empty">
          <div class="geofence-empty-icon">📍</div>
          <oxd-text tag="p"
            >Belum ada zona geofence. Klik "Tambah Zona" untuk mulai.</oxd-text
          >
        </div>

        <div v-else class="geofence-zones-list">
          <div
            v-for="zone in zones"
            :key="zone.id"
            class="geofence-zone-card"
            :class="{'geofence-zone-card--inactive': !zone.isActive}"
          >
            <div class="zone-card-icon">📍</div>
            <div class="zone-card-info">
              <div class="zone-card-name">{{ zone.name }}</div>
              <div class="zone-card-coords">
                {{ zone.latitude.toFixed(6) }},
                {{ zone.longitude.toFixed(6) }} &nbsp;•&nbsp; Radius:
                {{ zone.radiusMeters }}m
              </div>
            </div>
            <div class="zone-card-status">
              <oxd-switch-input
                :model-value="zone.isActive"
                @update:model-value="toggleZone(zone, $event)"
              />
            </div>
            <div class="zone-card-actions">
              <oxd-icon-button
                name="pencil-fill"
                @click="openEditModal(zone)"
              />
              <oxd-icon-button name="trash-fill" @click="deleteZone(zone)" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <oxd-dialog v-if="showModal" @update:show="showModal = false">
      <template #title>
        {{ editingZone ? '✏️ Edit Zona' : '➕ Tambah Zona Baru' }}
      </template>
      <template #content>
        <oxd-form :loading="isSaving" @submit-valid="saveZone">
          <oxd-form-row>
            <oxd-grid :cols="1">
              <oxd-grid-item>
                <oxd-input-field
                  v-model="form.name"
                  label="Nama Zona"
                  placeholder="Contoh: Kantor Pusat Jakarta"
                  :rules="[required]"
                  required
                />
              </oxd-grid-item>
              <oxd-grid-item>
                <div class="form-coords-row">
                  <oxd-input-field
                    v-model="form.latitude"
                    label="Latitude"
                    placeholder="-6.2147"
                    :rules="[required]"
                    required
                  />
                  <oxd-input-field
                    v-model="form.longitude"
                    label="Longitude"
                    placeholder="106.8451"
                    :rules="[required]"
                    required
                  />
                </div>
                <div class="form-coords-hint">
                  💡 Klik pada peta di bawah untuk mengisi koordinat otomatis
                </div>
                <!-- Mini map for coordinate picking -->
                <div
                  id="geofence-modal-map"
                  ref="modalMapContainer"
                  class="geofence-modal-map"
                ></div>
              </oxd-grid-item>
              <oxd-grid-item>
                <oxd-input-field
                  v-model.number="form.radiusMeters"
                  label="Radius (meter)"
                  type="number"
                  placeholder="100"
                  :rules="[required]"
                  required
                />
              </oxd-grid-item>
            </oxd-grid>
          </oxd-form-row>
          <oxd-form-actions>
            <oxd-button
              label="Batal"
              display-type="ghost"
              @click="closeModal"
            />
            <submit-button
              :label="editingZone ? 'Simpan Perubahan' : 'Tambah Zona'"
            />
          </oxd-form-actions>
        </oxd-form>
      </template>
    </oxd-dialog>

    <delete-confirmation ref="deleteDialog" />
  </div>
</template>

<script>
import {APIService} from '@/core/util/services/api.service';
import {required} from '@/core/util/validation/rules';
import {OxdSwitchInput, OxdSpinner} from '@ohrm/oxd';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';

const LEAFLET_CSS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
const LEAFLET_JS = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';

const DEFAULT_CENTER = [-6.2088, 106.8456]; // Jakarta
const DEFAULT_ZOOM = 13;

export default {
  name: 'GeofenceConfiguration',

  components: {
    'oxd-switch-input': OxdSwitchInput,
    'oxd-loading-spinner': OxdSpinner,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/attendance/geofence-zones',
    );
    return {http};
  },

  data() {
    return {
      zones: [],
      isLoading: false,
      isSaving: false,
      showModal: false,
      editingZone: null,
      mainMap: null,
      modalMap: null,
      circles: {},
      markers: {},
      modalCircle: null,
      modalMarker: null,
      form: this.emptyForm(),
      required,
    };
  },

  async mounted() {
    await this.loadLeaflet();
    await this.fetchZones();
    this.$nextTick(() => this.initMainMap());
  },

  beforeUnmount() {
    if (this.mainMap) this.mainMap.remove();
    if (this.modalMap) this.modalMap.remove();
  },

  methods: {
    emptyForm() {
      return {name: '', latitude: '', longitude: '', radiusMeters: 100};
    },

    loadLeaflet() {
      return new Promise((resolve) => {
        if (window.L) {
          resolve();
          return;
        }
        // Load CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = LEAFLET_CSS;
        document.head.appendChild(link);
        // Load JS
        const script = document.createElement('script');
        script.src = LEAFLET_JS;
        script.onload = resolve;
        document.head.appendChild(script);
      });
    },

    initMainMap() {
      const L = window.L;
      if (!L || this.mainMap) return;

      this.mainMap = L.map('geofence-leaflet-map').setView(
        DEFAULT_CENTER,
        DEFAULT_ZOOM,
      );
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors',
        maxZoom: 19,
      }).addTo(this.mainMap);

      // Click to add zone
      this.mainMap.on('click', (e) => {
        const {lat, lng} = e.latlng;
        this.form.latitude = lat.toFixed(8);
        this.form.longitude = lng.toFixed(8);
        this.openAddModal();
      });

      this.renderZonesOnMap();
    },

    initModalMap() {
      this.$nextTick(() => {
        const L = window.L;
        if (!L) return;

        // Destroy existing modal map
        if (this.modalMap) {
          this.modalMap.remove();
          this.modalMap = null;
        }

        const center =
          this.form.latitude && this.form.longitude
            ? [parseFloat(this.form.latitude), parseFloat(this.form.longitude)]
            : DEFAULT_CENTER;

        this.modalMap = L.map('geofence-modal-map').setView(center, 15);
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap',
          maxZoom: 19,
        }).addTo(this.modalMap);

        if (this.form.latitude && this.form.longitude) {
          this.updateModalMapMarker();
        }

        this.modalMap.on('click', (e) => {
          const {lat, lng} = e.latlng;
          this.form.latitude = lat.toFixed(8);
          this.form.longitude = lng.toFixed(8);
          this.updateModalMapMarker();
        });
      });
    },

    updateModalMapMarker() {
      const L = window.L;
      if (!this.modalMap || !this.form.latitude || !this.form.longitude) return;
      const latlng = [
        parseFloat(this.form.latitude),
        parseFloat(this.form.longitude),
      ];
      const radius = parseInt(this.form.radiusMeters) || 100;

      if (this.modalMarker) this.modalMarker.remove();
      if (this.modalCircle) this.modalCircle.remove();

      this.modalMarker = L.marker(latlng).addTo(this.modalMap);
      this.modalCircle = L.circle(latlng, {
        radius,
        color: '#1565c0',
        fillColor: '#42a5f5',
        fillOpacity: 0.2,
      }).addTo(this.modalMap);

      this.modalMap.setView(latlng, 15);
    },

    renderZonesOnMap() {
      const L = window.L;
      if (!L || !this.mainMap) return;

      // Clear existing
      Object.values(this.circles).forEach((c) => c.remove());
      Object.values(this.markers).forEach((m) => m.remove());
      this.circles = {};
      this.markers = {};

      this.zones.forEach((zone) => {
        const latlng = [zone.latitude, zone.longitude];
        const color = zone.isActive ? '#2e7d32' : '#9e9e9e';

        this.circles[zone.id] = L.circle(latlng, {
          radius: zone.radiusMeters,
          color,
          fillColor: zone.isActive ? '#4caf50' : '#bdbdbd',
          fillOpacity: 0.2,
        }).addTo(this.mainMap);

        this.markers[zone.id] = L.marker(latlng)
          .bindPopup(`<b>${zone.name}</b><br>Radius: ${zone.radiusMeters}m`)
          .addTo(this.mainMap);
      });
    },

    async fetchZones() {
      this.isLoading = true;
      try {
        const res = await this.http.getAll();
        this.zones = res.data.data;
        if (this.mainMap) this.renderZonesOnMap();
      } finally {
        this.isLoading = false;
      }
    },

    openAddModal() {
      this.editingZone = null;
      this.showModal = true;
      this.$nextTick(() => this.initModalMap());
    },

    openEditModal(zone) {
      this.editingZone = zone;
      this.form = {
        name: zone.name,
        latitude: String(zone.latitude),
        longitude: String(zone.longitude),
        radiusMeters: zone.radiusMeters,
      };
      this.showModal = true;
      this.$nextTick(() => {
        this.initModalMap();
        this.updateModalMapMarker();
      });
    },

    closeModal() {
      this.showModal = false;
      this.editingZone = null;
      this.form = this.emptyForm();
      if (this.modalMap) {
        this.modalMap.remove();
        this.modalMap = null;
      }
    },

    async saveZone() {
      this.isSaving = true;
      try {
        const payload = {...this.form};
        if (this.editingZone) {
          await this.http.update(this.editingZone.id, payload);
          this.$toast.saveSuccess();
        } else {
          await this.http.create(payload);
          this.$toast.saveSuccess();
        }
        this.closeModal();
        await this.fetchZones();
      } finally {
        this.isSaving = false;
      }
    },

    async toggleZone(zone, newValue) {
      await this.http.update(zone.id, {...zone, isActive: newValue});
      zone.isActive = newValue;
      this.renderZonesOnMap();
    },

    async deleteZone(zone) {
      const confirmed = await this.$refs.deleteDialog.showDialog();
      if (confirmed === 'ok') {
        await this.http.deleteById([zone.id]);
        this.$toast.deleteSuccess();
        await this.fetchZones();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.geofence-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
}

.geofence-subtitle {
  color: #607d8b;
  font-size: 13px;
  margin-top: 4px;
}

.geofence-map-section {
  margin: 0 -24px;
}

.geofence-map-label {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 24px 10px;
  font-size: 13px;
  color: #546e7a;
}

.geofence-map-badge {
  background: linear-gradient(135deg, #1565c0, #42a5f5);
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
}

.geofence-map-container {
  height: 420px;
  width: 100%;
  z-index: 1;
}

.geofence-modal-map {
  height: 220px;
  width: 100%;
  border-radius: 10px;
  margin-top: 12px;
  z-index: 1;
  border: 1px solid #e0e0e0;
}

.geofence-section-title {
  font-weight: 600;
  color: #37474f;
  margin-bottom: 16px;
}

.geofence-loading {
  display: flex;
  justify-content: center;
  padding: 32px;
}

.geofence-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px;
  color: #90a4ae;
  text-align: center;
}

.geofence-empty-icon {
  font-size: 40px;
}

.geofence-zones-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.geofence-zone-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: white;
  border: 1px solid #e8eaf6;
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);

  &:hover {
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
    border-color: #c5cae9;
  }

  &--inactive {
    opacity: 0.6;
    background: #fafafa;
  }
}

.zone-card-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.zone-card-info {
  flex: 1;
}

.zone-card-name {
  font-weight: 600;
  color: #263238;
  font-size: 14px;
}

.zone-card-coords {
  font-size: 12px;
  color: #78909c;
  margin-top: 2px;
  font-family: monospace;
}

.zone-card-status {
  flex-shrink: 0;
}

.zone-card-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.form-coords-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.form-coords-hint {
  font-size: 12px;
  color: #78909c;
  margin-top: 6px;
}
</style>
